import { inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { getOrganizationFromPath } from '../shared/utils/route-utils';

/** First URL segment of the admin back-office subtree (`/admin/...`). */
export const ADMIN_PATH = 'admin';

/**
 * Default landing page inside the tenant portal (`/:org/<this>`). Every
 * "take the user to their org" navigation goes here — the org-root redirect,
 * the bare-"/" redirect, and the user-card portal switch.
 */
export const TENANT_HOME_PATH = 'monitoring';

export type Portal = 'admin' | 'tenant';

const PORTAL_STORAGE_KEY = 'preferredPortal';

/**
 * The portal the user last worked in, or null if never recorded (or storage is
 * unavailable). Consulted only by the bare-"/" redirect in app.routes.ts so a
 * super-admin lands back where they left off — explicit URLs always win.
 */
export function readStoredPortal(): Portal | null {
  try {
    const stored = localStorage.getItem(PORTAL_STORAGE_KEY);
    return stored === 'admin' || stored === 'tenant' ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Top-level route segments that are NOT organization names. The org-change
 * watcher in ContextService must skip these, or it treats e.g. "admin" as an
 * org and redirects to not-found. Keep in sync with the non-":organizationName"
 * top-level paths in app.routes.ts.
 */
export const NON_ORG_ROUTES = new Set<string>([
  ADMIN_PATH,
  'not-found',
  'no-organization',
  'load-failed',
]);

/**
 * Single source of truth for "which portal are we in?". Derived from the first
 * URL segment and recomputed on every navigation. Centralized here so the
 * `'admin'` rule lives in one place instead of being re-derived in the navbar,
 * topbar, and user-card.
 *
 * Also persists the last-visited portal to localStorage so the bare-"/"
 * redirect can land the user where they last worked (see readStoredPortal).
 */
@Injectable({ providedIn: 'root' })
export class PortalService {
  private readonly router = inject(Router);

  private readonly firstSegment = (): string =>
    getOrganizationFromPath(this.router);

  constructor() {
    // Remember which portal the user works in (seeded with the current URL,
    // then every navigation). Roles are irrelevant here: non-super-admins only
    // ever record 'tenant', and the root redirect ignores it for them anyway.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(),
      )
      .subscribe(() => storePortal(this.firstSegment()));
  }

  /** True when the current URL is under the `/admin` back-office subtree. */
  readonly inAdminPortal: Signal<boolean> = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.firstSegment() === ADMIN_PATH),
      // NavigationEnd only fires on *later* navigations, so seed with the
      // current URL for the initial render.
      startWith(this.firstSegment() === ADMIN_PATH),
    ),
    { initialValue: this.firstSegment() === ADMIN_PATH },
  );
}

/**
 * Persist which portal a URL belongs to. Error pages and the bare root are
 * neither portal, so they leave the stored preference untouched.
 */
function storePortal(firstSegment: string): void {
  const portal: Portal | null =
    firstSegment === ADMIN_PATH
      ? 'admin'
      : firstSegment && !NON_ORG_ROUTES.has(firstSegment)
        ? 'tenant'
        : null;
  if (!portal) {
    return;
  }
  try {
    localStorage.setItem(PORTAL_STORAGE_KEY, portal);
  } catch {
    // Storage unavailable (private mode / locked down) — the session still
    // works, the preference just isn't remembered. Same policy as ThemeService.
  }
}
